import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Shield, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { packages } from '../data/mock';
import CTAButtons from '../components/CTAButtons';
import RegistrationModal from '../components/RegistrationModal';

const TrademarkRegistration = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const benefits = [
    'Legal protection for your brand name, logo, or slogan',
    'Exclusive rights to use your trademark nationwide',
    'Ability to take legal action against infringement',
    'Increased brand value and credibility',
    'Protection against unauthorized use by competitors',
    'Renewable protection that can last indefinitely'
  ];

  const process = [
    {
      step: 1,
      title: 'Free Consultation',
      description: 'We start with a thorough review of your trademark needs and eligibility.'
    },
    {
      step: 2,
      title: 'Comprehensive Search',
      description: 'Our experts conduct an in-depth search to ensure your trademark is available.'
    },
    {
      step: 3,
      title: 'Application Preparation',
      description: 'We prepare and review your complete trademark application for accuracy.'
    },
    {
      step: 4,
      title: 'USPTO Filing',
      description: 'We file your application with the United States Patent and Trademark Office.'
    },
    {
      step: 5,
      title: 'Ongoing Support',
      description: 'We monitor your application and handle any office actions or communications.'
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Trademark Registration Services - USPTO Filing from $45 | Global Trademark Pro</title>
        <meta name="description" content="Register your trademark with USPTO from just $45. Professional trademark filing services with expert guidance. Fast processing, high success rate, and comprehensive brand protection." />
        <meta name="keywords" content="trademark registration, USPTO trademark filing, register trademark online, trademark attorney, trademark application, brand protection" />
        <link rel="canonical" href="https://yourdomain.com/trademark-registration" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
                Trademark Registration
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Protect Your Brand with Trademark Registration
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Secure exclusive rights to your brand name, logo, or slogan. Our expert team makes trademark registration simple, affordable, and stress-free.
              </p>
              <CTAButtons />
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
                alt="Trademark registration"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Trademark */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">What is a Trademark?</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              A trademark is a unique symbol, word, phrase, design, or combination that identifies and distinguishes your goods or services from those of others. It's your brand's signature—the mark that customers associate with your business.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Registering your trademark with the USPTO provides legal protection and exclusive rights to use that mark in connection with your products or services throughout the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Register Your Trademark?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <p className="text-gray-700 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Our Trademark Registration Process
            </h2>
            <div className="space-y-6">
              {process.map((item, index) => (
                <div key={index} className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trademark Registration Packages
            </h2>
            <p className="text-xl text-gray-600">Choose the package that best fits your needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {packages.trademark.map((pkg, index) => (
              <Card key={index} className={`relative transition-all duration-300 hover:shadow-xl ${pkg.featured ? 'border-2 border-emerald-500 shadow-lg scale-105' : 'hover:border-emerald-200'}`}>
                {pkg.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-1 rounded-full text-xl font-semibold">
                    Most Popular
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <CardDescription className="text-3xl font-bold text-gray-900">
                    ${pkg.price}<span className="text-xl text-gray-500">*</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="feature-list space-y-0">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx}>
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="text-base text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xl text-gray-500 mt-4">*(Govt. filing fee not included)</p>
                </CardContent>
                <CardFooter>
                  <Button onClick={() => setIsModalOpen(true)} className={`w-full ${pkg.featured ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' : 'bg-gray-900 hover:bg-gray-800'}`}>
                    Register My Trademark
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-emerald-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Don't wait to secure your trademark. Start your application today with Global Trademark Pro.
          </p>
          <CTAButtons className="justify-center" variant="dark" />
        </div>
      </section>
      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default TrademarkRegistration;